import React, { useMemo, useState, useCallback } from 'react';
import { Card, Row, Col, Form, Table } from 'react-bootstrap';
import moment from 'moment';
import './RegressionsTab.scss';

const RegressionsTab = ({ pipelines }) => {
  const [selectedDate, setSelectedDate] = useState('all');
  const [testReports, setTestReports] = useState({});
  const [loadingTestReports, setLoadingTestReports] = useState(false);
  const [durationUnit, setDurationUnit] = useState('m'); // 's', 'm', 'h'
  const [sortConfig, setSortConfig] = useState({ key: 'startTime', direction: 'asc' });
  
  // Function to fetch test report for a QA pipeline
  const fetchTestReport = useCallback(async (qaPipelineId) => {
    if (!qaPipelineId) return null;
    
    try {
      const response = await fetch(`https://gitlab.devpayever.com/api/v4/projects/453/pipelines/${qaPipelineId}/test_report`, {
        headers: {
          'PRIVATE-TOKEN': 'glpat-teEmxgkyy6FvWApYvyDL',
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.log(`[DEBUG] Test report API not available for pipeline ${qaPipelineId}, status: ${response.status}`);
        return null;
      }
    } catch (error) {
      console.error(`[DEBUG] Error fetching test report for pipeline ${qaPipelineId}:`, error);
      return null;
    }
  }, []);

  // Function to fetch test reports for all pipelines
  const fetchAllTestReports = useCallback(async (pipelineData) => {
    setLoadingTestReports(true);
    const reports = {};
    
    try {
      // Process pipelines in batches to avoid overwhelming the API
      const batchSize = 5;
      for (let i = 0; i < pipelineData.length; i += batchSize) {
        const batch = pipelineData.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (pipeline) => {
          if (pipeline.qaPipelineId) {
            const report = await fetchTestReport(pipeline.qaPipelineId);
            if (report) {
              reports[pipeline.id] = report;
            }
          }
        }));
      }
      
      setTestReports(reports);
      console.log('[DEBUG] Fetched test reports:', reports);
    } catch (error) {
      console.error('[DEBUG] Error fetching test reports:', error);
    } finally {
      setLoadingTestReports(false);
    }
  }, [fetchTestReport]);

  // Calculate average tests per duration for pipelines with test reports
  const calculateAverageTestsPerDuration = useCallback((pipelineData, unit = 's') => {
    const pipelinesWithReports = pipelineData.filter(p => testReports[p.id]);
    
    if (pipelinesWithReports.length === 0) return 0;
    
    let totalTests = 0;
    let totalDuration = 0;
    
    pipelinesWithReports.forEach(pipeline => {
      const report = testReports[pipeline.id];
      if (report && report.total_count) {
        totalTests += report.total_count;
        totalDuration += pipeline.duration;
      }
    });
    
    if (totalDuration === 0) return 0;
    
    // Convert duration to the selected unit
    let durationInUnit = totalDuration;
    if (unit === 'm') durationInUnit = totalDuration / 60;
    else if (unit === 'h') durationInUnit = totalDuration / 3600;
    
    return totalTests / durationInUnit;
  }, [testReports]);
  
  // Function to check if a pipeline start time would cause groups sum overload (>12)
  const checkGroupsOverload = useCallback((pipeline, allPipelines) => {
    const pipelineStartTime = moment(pipeline.startTime);
    
    // Calculate groups sum at the pipeline's start time
    let groupsSum = 0;
    
    allPipelines.forEach(otherPipeline => {
      if (otherPipeline.id === pipeline.id) {
        // Include the current pipeline's groups
        groupsSum += pipeline.groups.length;
      } else {
        // Check if other pipeline is running at this time
        const otherStart = moment(otherPipeline.startTime);
        const otherEnd = moment(otherPipeline.endTime);
        
        if (pipelineStartTime.isBetween(otherStart, otherEnd, null, '[]')) {
          groupsSum += otherPipeline.groups.length;
        }
      }
    });
    
    return groupsSum > 12;
    }, []);
  
  // Sorting function for table
  const sortTableData = useCallback((data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'groups':
          aValue = a.groups.length;
          bValue = b.groups.length;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'startTime':
          aValue = new Date(a.startTime);
          bValue = new Date(b.startTime);
          break;
        case 'endTime':
          aValue = new Date(a.endTime);
          bValue = new Date(b.endTime);
          break;
        case 'duration':
          aValue = a.duration;
          bValue = b.duration;
          break;
        case 'testsQuantity':
          const aTestReport = testReports[a.id];
          const bTestReport = testReports[b.id];
          
          // Use a fallback average if chartData is not available yet
          let averageTestsPerDuration = 0;
          if (data && data.length > 0) {
            const totalDuration = data.reduce((sum, p) => sum + p.duration, 0);
            const totalTests = Object.values(testReports).reduce((sum, report) => sum + (report?.total_count || 0), 0);
            if (totalDuration > 0) {
              let durationInUnit = totalDuration;
              if (durationUnit === 'm') durationInUnit = totalDuration / 60;
              else if (durationUnit === 'h') durationInUnit = totalDuration / 3600;
              averageTestsPerDuration = totalTests / durationInUnit;
            }
          }
          
          aValue = aTestReport ? aTestReport.total_count : 
            Math.round((a.duration / (durationUnit === 'm' ? 60 : durationUnit === 'h' ? 3600 : 1)) * averageTestsPerDuration);
          bValue = bTestReport ? bTestReport.total_count : 
            Math.round((b.duration / (durationUnit === 'm' ? 60 : durationUnit === 'h' ? 3600 : 1)) * averageTestsPerDuration);
          break;
        case 'testDuration':
          aValue = a.totalTestDuration;
          bValue = b.totalTestDuration;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [sortConfig, testReports, durationUnit]);
  
  // Handle sort change
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Generate consistent colors for each pipeline based on its ID
  const getPipelineColor = (pipelineId) => {
    // Use a simple hash function to get consistent colors for the same pipeline
    const hash = pipelineId.toString().split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const hue = Math.abs(hash) % 360;
    const saturation = 50 + (Math.abs(hash) % 30); // 50-80% saturation
    const lightness = 40 + (Math.abs(hash) % 20); // 40-60% lightness
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };
  
  // Add debugging logs
  console.log('[DEBUG] RegressionsTab received pipelines:', pipelines);
  console.log('[DEBUG] Pipelines length:', pipelines?.length);
  
  // Get available dates from pipeline start times
  const availableDates = useMemo(() => {
    if (!pipelines || pipelines.length === 0) return [];
    
    const dates = new Set();
    pipelines.forEach(pipeline => {
      if (pipeline.details?.started_at) {
        const date = moment(pipeline.details.started_at).format('YYYY-MM-DD');
        dates.add(date);
      }
    });
    
    return Array.from(dates).sort().reverse(); // Most recent first
  }, [pipelines]);
  
  // Filter and process pipeline data for the chart
  const chartData = useMemo(() => {
    if (!pipelines || pipelines.length === 0) {
      console.log('[DEBUG] No pipelines data available');
      return [];
    }

    console.log('[DEBUG] Processing pipelines for chart...');
    
    // First, let's see what we have without filtering
    console.log('[DEBUG] All pipelines sources:', [...new Set(pipelines.map(p => p.source))]);
    console.log('[DEBUG] All pipelines statuses:', [...new Set(pipelines.map(p => p.status))]);
    console.log('[DEBUG] Pipelines with details.started_at:', pipelines.filter(p => p.details?.started_at).length);
    console.log('[DEBUG] Pipelines with details.finished_at:', pipelines.filter(p => p.details?.finished_at).length);
    
    // Filter for scheduled pipelines that have started (including running ones)
    let scheduledPipelines = pipelines.filter(pipeline => {
      const isScheduled = pipeline.source === 'schedule';
      const hasStarted = pipeline.status !== 'pending' && 
                         pipeline.status !== 'created';
      const hasStartTime = pipeline.details?.started_at;
      
      console.log(`[DEBUG] Pipeline ${pipeline.id}: source=${pipeline.source}, status=${pipeline.status}, details.started_at=${pipeline.details?.started_at}, details.finished_at=${pipeline.details?.finished_at}`);
      console.log(`[DEBUG] Pipeline ${pipeline.id}: isScheduled=${isScheduled}, hasStarted=${hasStarted}, hasStartTime=${hasStartTime}`);
      
      return isScheduled && hasStarted && hasStartTime;
    });

    // Apply date filter if a specific date is selected
    if (selectedDate !== 'all') {
      scheduledPipelines = scheduledPipelines.filter(pipeline => {
        const pipelineDate = moment(pipeline.details.started_at).format('YYYY-MM-DD');
        return pipelineDate === selectedDate;
      });
      console.log(`[DEBUG] After date filtering (${selectedDate}):`, scheduledPipelines.length, 'pipelines');
    }

    console.log('[DEBUG] Scheduled pipelines found:', scheduledPipelines.length);
    console.log('[DEBUG] Scheduled pipelines:', scheduledPipelines);
    
    // Debug specific pipeline 460136
    const pipeline460136 = pipelines.find(p => p.id === 460136);
    if (pipeline460136) {
      console.log('[DEBUG] Pipeline 460136 details:', {
        id: pipeline460136.id,
        source: pipeline460136.source,
        status: pipeline460136.status,
        started_at: pipeline460136.details?.started_at,
        finished_at: pipeline460136.details?.finished_at,
        isScheduled: pipeline460136.source === 'schedule',
        hasStarted: pipeline460136.status !== 'pending' && pipeline460136.status !== 'created',
        hasStartTime: !!pipeline460136.details?.started_at
      });
    } else {
      console.log('[DEBUG] Pipeline 460136 not found in original pipelines data');
    }

    // If no scheduled pipelines, let's show all completed pipelines for debugging
    if (scheduledPipelines.length === 0) {
      console.log('[DEBUG] No scheduled pipelines found, showing all completed pipelines for debugging');
      const allCompletedPipelines = pipelines.filter(pipeline => {
        const isCompleted = pipeline.status !== 'running' && 
                           pipeline.status !== 'pending' && 
                           pipeline.status !== 'created';
        const hasTimestamps = pipeline.details?.started_at && pipeline.details?.finished_at;
        return isCompleted && hasTimestamps;
      });
      console.log('[DEBUG] All completed pipelines:', allCompletedPipelines);
    }

    // Sort by tests quantity first (descending), then by start time (ascending) for better overlap organization
    scheduledPipelines.sort((a, b) => {
      // Calculate tests quantity for both pipelines
      const aTestsQuantity = a.jobs?.pipelineJobs?.reduce((sum, job) => {
        return sum + (job.mochawesome?.stats?.tests || 0);
      }, 0) || 0;
      
      const bTestsQuantity = b.jobs?.pipelineJobs?.reduce((sum, job) => {
        return sum + (job.mochawesome?.stats?.tests || 0);
      }, 0) || 0;
      
      // First sort by tests quantity (descending) - pipelines with fewer tests appear on top
      if (aTestsQuantity !== bTestsQuantity) {
        return bTestsQuantity - aTestsQuantity; // Descending order
      }
      
      // If tests quantities are the same, sort by start time (ascending)
      const aStartTime = moment(a.details.started_at);
      const bStartTime = moment(b.details.started_at);
      
      return aStartTime.valueOf() - bStartTime.valueOf();
    });

    console.log('[DEBUG] Sorted pipelines by tests quantity first, then start time:');
    scheduledPipelines.forEach((pipeline, index) => {
      const testsQuantity = pipeline.jobs?.pipelineJobs?.reduce((sum, job) => {
        return sum + (job.mochawesome?.stats?.tests || 0);
      }, 0) || 0;
      const startTime = moment(pipeline.details.started_at);
      console.log(`[DEBUG] Pipeline ${pipeline.id} (index ${index}): tests=${testsQuantity}, start=${startTime.format('HH:mm:ss.SSS')}`);
    });

    // Debug final chart data
    const finalChartData = scheduledPipelines.map(pipeline => {
      const startTime = moment(pipeline.details.started_at);
      const endTime = pipeline.details?.finished_at ? moment(pipeline.details.finished_at) : moment(); // Use current time for running pipelines
      const duration = endTime.diff(startTime, 'seconds');
      
      // Extract QA pipeline ID from qa_pipeline object
      let qaPipelineId = null;
      if (pipeline.qa_pipeline && pipeline.qa_pipeline.id) {
        qaPipelineId = pipeline.qa_pipeline.id;
      }
      
      // Calculate total test duration from mochawesome data if available
      let totalTestDuration = 0;
      if (pipeline.jobs && pipeline.jobs.pipelineJobs) {
        pipeline.jobs.pipelineJobs.forEach(job => {
          if (job.mochawesome && job.mochawesome.stats && job.mochawesome.stats.duration) {
            totalTestDuration += job.mochawesome.stats.duration / 1000; // Convert from ms to seconds
          }
        });
      }

      // Extract group information
      let groups = [];
      let groupsString = '';
      
      // Try to get groups from jobs data first
      if (pipeline.jobs && pipeline.jobs.groups) {
        groups = pipeline.jobs.groups;
        groupsString = groups.join(', ');
      }
      // Fallback to CI_GROUP variable if available
      else if (pipeline.CI_GROUP) {
        groups = [pipeline.CI_GROUP];
        groupsString = pipeline.CI_GROUP;
      }
      // Try to extract from variables if available
      else if (pipeline.variables) {
        const ciGroupVar = pipeline.variables.find(v => v.key === 'CI_GROUP');
        if (ciGroupVar) {
          groups = [ciGroupVar.value];
          groupsString = ciGroupVar.value;
        }
      }

      const processedPipeline = {
        id: pipeline.id,
        startTime: startTime.toDate(),
        endTime: endTime.toDate(),
        duration: duration,
        totalTestDuration: totalTestDuration,
        status: pipeline.status,
        name: pipeline.name || `Pipeline ${pipeline.id}`,
        groups: groups,
        groupsString: groupsString,
        qaPipelineId: qaPipelineId
      };
      
      console.log(`[DEBUG] Processed pipeline ${pipeline.id}:`, processedPipeline);
      
      return processedPipeline;
    });

    console.log('[DEBUG] Final chart data length:', finalChartData.length);
    console.log('[DEBUG] Final chart data IDs:', finalChartData.map(p => p.id));

    return finalChartData;
  }, [pipelines, selectedDate]);

  // Fetch test reports when chart data changes
  React.useEffect(() => {
    if (chartData.length > 0) {
      console.log('[DEBUG] Fetching test reports for', chartData.length, 'pipelines');
      console.log('[DEBUG] Chart data pipeline IDs:', chartData.map(p => p.id));
      fetchAllTestReports(chartData);
    }
  }, [chartData, fetchAllTestReports]);

  // Calculate chart dimensions and scales
  const chartConfig = useMemo(() => {
    if (chartData.length === 0) {
      return {
        width: 800,
        height: 400,
        margin: { top: 20, right: 30, bottom: 60, left: 60 }
      };
    }

    const width = 800;
    const height = 400;
    const margin = { top: 20, right: 80, bottom: 60, left: 60 }; // Increased right margin for second Y-axis
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Calculate time range
    const startTimes = chartData.map(d => d.startTime);
    const endTimes = chartData.map(d => d.endTime);
    const minTime = new Date(Math.min(...startTimes));
    const maxTime = new Date(Math.max(...endTimes));

    // Calculate tests quantity range including estimated quantities
    const averageTestsPerDuration = calculateAverageTestsPerDuration(chartData, durationUnit);
    const testsQuantities = chartData.map(d => {
      const testReport = testReports[d.id];
      if (testReport) {
        return testReport.total_count;
      } else {
        // Calculate estimated tests quantity based on duration proportion
        let durationInUnit = d.duration;
        if (durationUnit === 'm') durationInUnit = d.duration / 60;
        else if (durationUnit === 'h') durationInUnit = d.duration / 3600;
        return Math.round(durationInUnit * averageTestsPerDuration);
      }
    });
    const maxTestsQuantity = Math.max(...testsQuantities, 1); // At least 1 test

    // Calculate groups sum data points every 15 minutes
    const groupsSumData = (() => {
      const timePoints = [];
      const startTime = moment(minTime).startOf('hour');
      const endTime = moment(maxTime).endOf('hour');
      
      let currentTime = startTime.clone();
      while (currentTime.isBefore(endTime)) {
        // Add time points every 15 minutes
        for (let minute = 0; minute < 60; minute += 15) {
          const timePoint = currentTime.clone().add(minute, 'minutes');
          if (timePoint.isAfter(minTime) && timePoint.isBefore(maxTime)) {
            // Calculate sum of groups for pipelines running at this time point
            let groupsSum = 0;
            chartData.forEach(pipeline => {
              const pipelineStart = moment(pipeline.startTime);
              const pipelineEnd = moment(pipeline.endTime);
              
              if (timePoint.isBetween(pipelineStart, pipelineEnd, null, '[]')) {
                groupsSum += pipeline.groups.length;
              }
            });
            
            timePoints.push({
              time: timePoint.toDate(),
              groupsSum: groupsSum
            });
          }
        }
        currentTime.add(1, 'hour');
      }
      
      return timePoints;
    })();

    const maxGroupsSum = groupsSumData.length > 0 ? Math.max(...groupsSumData.map(d => d.groupsSum), 1) : 1;

    return {
      width,
      height,
      margin,
      chartWidth,
      chartHeight,
      minTime,
      maxTime,
      maxTestsQuantity,
      groupsSumData,
      maxGroupsSum
    };
  }, [chartData, testReports, calculateAverageTestsPerDuration, durationUnit]);

  // Render the chart
  const renderChart = () => {
    if (chartData.length === 0) {
      return (
        <div className="chart-container">
          <div className="no-data-message">
            <h5>No scheduled pipeline data available for charting.</h5>
            {selectedDate !== 'all' && (
              <p><strong>Date Filter:</strong> {moment(selectedDate).format('MMM DD, YYYY')}</p>
            )}
            <p>This could be because:</p>
            <ul>
              <li>No pipelines have been fetched yet</li>
              <li>All pipelines are from 'web' source instead of 'schedule'</li>
              <li>All pipelines are still running/pending</li>
              <li>Pipelines are missing start/end timestamps</li>
              {selectedDate !== 'all' && (
                <li>No scheduled pipelines exist for the selected date</li>
              )}
            </ul>
            <p><strong>Total pipelines available:</strong> {pipelines?.length || 0}</p>
            {pipelines && pipelines.length > 0 && (
              <>
                <p><strong>Pipeline sources:</strong> {[...new Set(pipelines.map(p => p.source))].join(', ')}</p>
                <p><strong>Pipeline statuses:</strong> {[...new Set(pipelines.map(p => p.status))].join(', ')}</p>
                <p><strong>Pipelines with timestamps:</strong> {pipelines.filter(p => p.details?.started_at && p.details?.finished_at).length}</p>
                <p><strong>Available dates:</strong> {availableDates.length} ({availableDates.slice(0, 5).map(d => moment(d).format('MMM DD')).join(', ')}{availableDates.length > 5 ? '...' : ''})</p>
              </>
            )}
          </div>
        </div>
      );
    }

    const { width, height, margin, chartWidth, chartHeight, minTime, maxTime, maxTestsQuantity, groupsSumData, maxGroupsSum } = chartConfig;

    // Calculate scales
    const timeScale = (time) => {
      const timeRange = maxTime - minTime;
      return margin.left + ((time - minTime) / timeRange) * chartWidth;
    };

    const durationScale = (quantity) => {
      return margin.top + chartHeight - (quantity / maxTestsQuantity) * chartHeight;
    };

    return (
      <div className="chart-container">
        <svg width={width} height={height} className="regressions-chart">
          {/* Chart background */}
          <rect
            x={margin.left}
            y={margin.top}
            width={chartWidth}
            height={chartHeight}
            fill="#f8f9fa"
            stroke="#dee2e6"
            strokeWidth="1"
          />

          {/* X-axis */}
          <line
            x1={margin.left}
            y1={margin.top + chartHeight}
            x2={margin.left + chartWidth}
            y2={margin.top + chartHeight}
            stroke="#6c757d"
            strokeWidth="2"
          />

          {/* Y-axis (left) */}
          <line
            x1={margin.left}
            y1={margin.top}
            x2={margin.left}
            y2={margin.top + chartHeight}
            stroke="#6c757d"
            strokeWidth="2"
          />

          {/* Y-axis (right) for groups sum */}
          <line
            x1={margin.left + chartWidth}
            y1={margin.top}
            x2={margin.left + chartWidth}
            y2={margin.top + chartHeight}
            stroke="#6c757d"
            strokeWidth="2"
          />

          {/* X-axis labels - show solid hours and 30 minutes */}
          {(() => {
            const timeRange = maxTime - minTime;
            
            // Generate time points for solid hours and 30 minutes
            const timePoints = [];
            const startHour = moment(minTime).startOf('hour');
            const endHour = moment(maxTime).endOf('hour');
            
            let currentTime = startHour.clone();
            while (currentTime.isBefore(endHour)) {
              // Add solid hour
              if (currentTime.isAfter(minTime) && currentTime.isBefore(maxTime)) {
                timePoints.push({
                  time: currentTime.toDate(),
                  isHour: true
                });
              }
              
              // Add 30 minutes past the hour
              const halfHour = currentTime.clone().add(30, 'minutes');
              if (halfHour.isAfter(minTime) && halfHour.isBefore(maxTime)) {
                timePoints.push({
                  time: halfHour.toDate(),
                  isHour: false
                });
              }
              
              currentTime.add(1, 'hour');
            }
            
            return timePoints.map((point, i) => {
              const ratio = (point.time - minTime) / timeRange;
            const x = margin.left + chartWidth * ratio;
              
            return (
              <g key={`x-label-${i}`}>
                <line
                  x1={x}
                  y1={margin.top + chartHeight}
                  x2={x}
                    y2={margin.top + chartHeight + (point.isHour ? 8 : 5)}
                  stroke="#6c757d"
                    strokeWidth={point.isHour ? "2" : "1"}
                />
                <text
                  x={x}
                    y={margin.top + chartHeight + 25}
                  textAnchor="middle"
                    fontSize={point.isHour ? "12" : "10"}
                    fontWeight={point.isHour ? "bold" : "normal"}
                  fill="#6c757d"
                >
                    {point.isHour 
                      ? moment(point.time).format('HH:mm')
                      : moment(point.time).format('mm')
                    }
                </text>
              </g>
            );
            });
          })()}

          {/* Y-axis labels (left) - Tests Quantity */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
            const testsQuantity = maxTestsQuantity * (1 - ratio);
            const y = margin.top + chartHeight * ratio;
            return (
              <g key={`y-label-left-${i}`}>
                <line
                  x1={margin.left}
                  y1={y}
                  x2={margin.left - 5}
                  y2={y}
                  stroke="#6c757d"
                  strokeWidth="1"
                />
                                  <text
                    x={margin.left - 10}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="12"
                    fill="#6c757d"
                  >
                    {Math.round(testsQuantity)}
                  </text>
              </g>
            );
          })}

          {/* Y-axis labels (right) - Groups Sum */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
            const groupsSum = maxGroupsSum * (1 - ratio);
            const y = margin.top + chartHeight * ratio;
            return (
              <g key={`y-label-right-${i}`}>
                <line
                  x1={margin.left + chartWidth}
                  y1={y}
                  x2={margin.left + chartWidth + 5}
                  y2={y}
                  stroke="#6c757d"
                  strokeWidth="1"
                />
                <text
                  x={margin.left + chartWidth + 10}
                  y={y + 4}
                  textAnchor="start"
                  fontSize="12"
                  fill="#6c757d"
                >
                  {Math.round(groupsSum)}
                </text>
              </g>
            );
          })}

          {/* Pipeline bars */}
          {chartData.map((pipeline, index) => {
            const x = timeScale(pipeline.startTime);
            const testReport = testReports[pipeline.id];
            const averageTestsPerDuration = calculateAverageTestsPerDuration(chartData, durationUnit);
            
            let testsQuantity = 0;
            let isEstimated = false;
            
            if (testReport) {
              testsQuantity = testReport.total_count;
            } else {
              // Calculate estimated tests quantity based on duration proportion
              let durationInUnit = pipeline.duration;
              if (durationUnit === 'm') durationInUnit = pipeline.duration / 60;
              else if (durationUnit === 'h') durationInUnit = pipeline.duration / 3600;
              testsQuantity = Math.round(durationInUnit * averageTestsPerDuration);
              isEstimated = true;
            }
            
            const barHeight = chartHeight - (durationScale(testsQuantity) - margin.top);
            const y = margin.top + chartHeight - barHeight;
            const barWidth = timeScale(pipeline.endTime) - x;

            // Debug chart rendering for each pipeline
            console.log(`[DEBUG] Rendering pipeline ${pipeline.id}:`, {
              index,
              x,
              y,
              barWidth,
              barHeight,
              testsQuantity,
              startTime: pipeline.startTime,
              endTime: pipeline.endTime,
              duration: pipeline.duration,
              isEstimated,
              isRunning: pipeline.status === 'running'
            });

            // Use consistent color for each pipeline based on its ID
            const barColor = getPipelineColor(pipeline.id);
            const isRunning = pipeline.status === 'running';

            // Check if bar dimensions are valid
            const isValidBar = barWidth > 0 && barHeight > 0 && !isNaN(x) && !isNaN(y);
            console.log(`[DEBUG] Pipeline ${pipeline.id} bar validity:`, {
              isValidBar,
              barWidth,
              barHeight,
              x,
              y,
              chartWidth,
              chartHeight
            });

            if (!isValidBar) {
              console.warn(`[DEBUG] Pipeline ${pipeline.id} has invalid bar dimensions - skipping render`);
              return null;
            }

            return (
              <g key={`pipeline-${pipeline.id}`} className="pipeline-bar">
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={barColor}
                  opacity={isEstimated ? "0.6" : isRunning ? "0.9" : "0.85"}
                  stroke={isRunning ? "#ffc107" : "#fff"}
                  strokeWidth={isRunning ? "2" : "1"}
                  strokeDasharray={isEstimated ? "5,5" : isRunning ? "8,4" : "none"}
                  style={{ pointerEvents: 'all' }}
                />
                
                {/* Groups count and pipeline ID label above the bar */}
                {pipeline.groups && pipeline.groups.length > 0 && (
                  <text
                    x={x + barWidth / 2}
                    y={y - 5}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight="bold"
                    fill="#495057"
                    className="groups-count-label"
                  >
                    {pipeline.groups.length} ({pipeline.id})
                  </text>
                )}
                
                <title>
                  {pipeline.name}
                  {'\n'}Groups: {pipeline.groupsString || 'N/A'} ({pipeline.groups.length})
                  {'\n'}Status: {pipeline.status}
                  {'\n'}Start: {moment(pipeline.startTime).format('YYYY-MM-DD HH:mm:ss')}
                  {'\n'}End: {isRunning ? 'Running...' : moment(pipeline.endTime).format('YYYY-MM-DD HH:mm:ss')}
                  {'\n'}Duration: {pipeline.duration}s{isRunning ? ' (ongoing)' : ''}
                  {'\n'}Tests Quantity: {isEstimated ? `${testsQuantity} (estimated)` : testsQuantity}
                  {'\n'}Test Duration: {Math.round(pipeline.totalTestDuration)}s
                </title>
              </g>
            );
          })}

          {/* Groups sum line graph */}
          {groupsSumData.length > 1 && (
            <g className="groups-sum-line">
              <path
                d={(() => {
                  const points = groupsSumData.map((point, index) => {
                    const x = timeScale(point.time);
                    const y = margin.top + chartHeight - (point.groupsSum / maxGroupsSum) * chartHeight;
                    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                  }).join(' ');
                  return points;
                })()}
                stroke="#e74c3c"
                strokeWidth="2"
                fill="none"
                opacity="0.8"
              />
              {/* Data points */}
              {groupsSumData.map((point, index) => {
                const x = timeScale(point.time);
                const y = margin.top + chartHeight - (point.groupsSum / maxGroupsSum) * chartHeight;
                return (
                  <circle
                    key={`groups-point-${index}`}
                    cx={x}
                    cy={y}
                    r="3"
                    fill="#e74c3c"
                    opacity="0.8"
                  />
                );
              })}
            </g>
          )}

          {/* Chart labels */}
          <text
            x={width / 2}
            y={height - 10}
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="#495057"
          >
            Pipeline Start Time
          </text>
          <text
            x={20}
            y={height / 2}
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="#495057"
            transform={`rotate(-90, 20, ${height / 2})`}
          >
            Tests Quantity
          </text>
          <text
            x={width - 20}
            y={height / 2}
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="#495057"
            transform={`rotate(-90, ${width - 20}, ${height / 2})`}
          >
            Groups Sum
          </text>
          
          {/* Display average tests per duration */}
          {(() => {
            const avgTestsPerDuration = calculateAverageTestsPerDuration(chartData, durationUnit);
            const unitLabel = durationUnit === 's' ? 'second' : durationUnit === 'm' ? 'minute' : 'hour';
            return (
              <text
                x={width - 10}
                y={margin.top + 20}
                textAnchor="end"
                fontSize="12"
                fill="#6c757d"
              >
                Avg: {avgTestsPerDuration.toFixed(2)} tests/{unitLabel}
              </text>
            );
          })()}
        </svg>
      </div>
    );
  };

  return (
    <>
      <Card>
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <h4>Pipeline Performance Regressions</h4>
            <p className="text-muted mb-0">
              Chart showing scheduled pipeline performance over time. 
              Bar width represents pipeline running time, height represents total test duration.
            </p>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label htmlFor="dateSelector" className="mb-1">
                <strong>Filter by Date:</strong>
              </Form.Label>
              <Form.Select
                id="dateSelector"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                size="sm"
                style={{ minWidth: '150px' }}
              >
                <option value="all">All Dates ({availableDates.length} available)</option>
                {availableDates.map(date => (
                  <option key={date} value={date}>
                    {moment(date).format('MMM DD, YYYY')} ({pipelines?.filter(p => 
                      p.details?.started_at && 
                      moment(p.details.started_at).format('YYYY-MM-DD') === date
                    ).length || 0} pipelines)
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label htmlFor="durationUnitSelector" className="mb-1">
                <strong>Duration Unit:</strong>
              </Form.Label>
              <Form.Select
                id="durationUnitSelector"
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value)}
                size="sm"
                style={{ minWidth: '100px' }}
              >
                <option value="s">Seconds</option>
                <option value="m">Minutes</option>
                <option value="h">Hours</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col>
            {renderChart()}
          </Col>
        </Row>
        
        {/* Summary statistics */}
        <Row className="mt-4">
          <Col md={3}>
            <div className="stat-card">
              <h5>Total Pipelines</h5>
              <p className="stat-value">{chartData.length}</p>
              {selectedDate !== 'all' && (
                <small className="text-muted">
                  Filtered for {moment(selectedDate).format('MMM DD, YYYY')}
                </small>
              )}
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-card">
              <h5>Average Tests per Pipeline</h5>
              <p className="stat-value">
                {(() => {
                  const pipelinesWithReports = Object.keys(testReports).length;
                  if (pipelinesWithReports > 0) {
                    const totalTests = Object.values(testReports).reduce((sum, report) => sum + (report?.total_count || 0), 0);
                    return Math.round(totalTests / pipelinesWithReports);
                  }
                  return 'N/A';
                })()}
              </p>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-card">
              <h5>Total Tests</h5>
              <p className="stat-value">
                {(() => {
                  const totalTests = Object.values(testReports).reduce((sum, report) => sum + (report?.total_count || 0), 0);
                  return totalTests > 0 ? totalTests : 'N/A';
                })()}
              </p>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-card">
              <h5>Time Range</h5>
              <p className="stat-value">
                {chartData.length > 0 
                  ? `${moment(chartConfig.minTime).format('MMM DD')} - ${moment(chartConfig.maxTime).format('MMM DD')}`
                  : 'N/A'
                }
              </p>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>

    {/* Pipeline Data Table */}
    <Card className="mt-4">
      <Card.Header>
        <h5>Scheduled Pipeline Details</h5>
        <p className="text-muted mb-0">
          Detailed view of all scheduled pipelines with their performance metrics.
        </p>
      </Card.Header>
      <Card.Body>
        {chartData.length > 0 ? (
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('id')}
                    style={{ cursor: 'pointer' }}
                  >
                    Pipeline ID
                    {sortConfig.key === 'id' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('name')}
                    style={{ cursor: 'pointer' }}
                  >
                    Name
                    {sortConfig.key === 'name' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('groups')}
                    style={{ cursor: 'pointer' }}
                  >
                    Groups
                    {sortConfig.key === 'groups' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    Status
                    {sortConfig.key === 'status' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('startTime')}
                    style={{ cursor: 'pointer' }}
                  >
                    Start Time
                    {sortConfig.key === 'startTime' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('endTime')}
                    style={{ cursor: 'pointer' }}
                  >
                    End Time
                    {sortConfig.key === 'endTime' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('duration')}
                    style={{ cursor: 'pointer' }}
                  >
                    Duration
                    {sortConfig.key === 'duration' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('testsQuantity')}
                    style={{ cursor: 'pointer' }}
                  >
                    Tests Quantity
                    {sortConfig.key === 'testsQuantity' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('testDuration')}
                    style={{ cursor: 'pointer' }}
                  >
                    Test Duration
                    {sortConfig.key === 'testDuration' && (
                      <span className="ms-1">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortTableData(chartData).map((pipeline) => {
                  return (
                    <tr key={pipeline.id}>
                      <td>
                        <a 
                          href={`https://gitlab.com/your-project/-/pipelines/${pipeline.id}`} 
                          target="_blank" 
                          rel="noreferrer"
                          className="text-decoration-none"
                        >
                          #{pipeline.id}
                        </a>
                      </td>
                      <td>{pipeline.name}</td>
                      <td>
                        {pipeline.groupsString ? (
                          <span 
                            className="badge bg-info text-dark" 
                            title={pipeline.groupsString}
                          >
                            {pipeline.groupsString.length > 20 
                              ? pipeline.groupsString.substring(0, 20) + '...' 
                              : pipeline.groupsString
                            }
                            <span className="ms-1 fw-bold">({pipeline.groups.length})</span>
                          </span>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${
                          pipeline.status === 'success' ? 'success' : 
                          pipeline.status === 'failed' ? 'danger' : 
                          pipeline.status === 'running' ? 'warning' : 
                          'secondary'
                        }`}>
                          {pipeline.status}
                          {pipeline.status === 'running' && <span className="ms-1">▶</span>}
                        </span>
                      </td>
                      <td 
                        className={checkGroupsOverload(pipeline, chartData) ? 'table-danger' : ''}
                        style={checkGroupsOverload(pipeline, chartData) ? { backgroundColor: '#f8d7da', color: '#721c24', fontWeight: 'bold' } : {}}
                        title={checkGroupsOverload(pipeline, chartData) ? 'This pipeline start time causes Groups Sum to exceed 10' : ''}
                      >
                        {moment(pipeline.startTime).format('MMM DD, YYYY HH:mm:ss')}
                      </td>
                      <td>
                        {pipeline.status === 'running' 
                          ? <span className="text-warning fw-bold">Running...</span>
                          : moment(pipeline.endTime).format('MMM DD, YYYY HH:mm:ss')
                        }
                      </td>
                      <td>
                        {(() => {
                          let durationValue = pipeline.duration;
                          let durationUnitLabel = 's';
                          
                          if (durationUnit === 'm') {
                            durationValue = pipeline.duration / 60;
                            durationUnitLabel = 'm';
                          } else if (durationUnit === 'h') {
                            durationValue = pipeline.duration / 3600;
                            durationUnitLabel = 'h';
                          }
                          
                          return (
                            <>
                              {durationValue.toFixed(1)}{durationUnitLabel}
                              {pipeline.status === 'running' && <span className="text-warning ms-1">(ongoing)</span>}
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        {(() => {
                          const testReport = testReports[pipeline.id];
                          const averageTestsPerDuration = calculateAverageTestsPerDuration(chartData, durationUnit);
                          
                          if (testReport) {
                            return (
                              <span className="badge bg-primary">
                                {testReport.total_count} tests
                              </span>
                            );
                          } else if (loadingTestReports) {
                            return <span className="text-muted">Loading...</span>;
                          } else {
                            // Calculate estimated tests quantity based on duration proportion
                            let durationInUnit = pipeline.duration;
                            if (durationUnit === 'm') durationInUnit = pipeline.duration / 60;
                            else if (durationUnit === 'h') durationInUnit = pipeline.duration / 3600;
                            const estimatedTests = Math.round(durationInUnit * averageTestsPerDuration);
                            
                            return (
                              <span className="badge bg-warning text-dark" title="Estimated based on duration">
                                ~{estimatedTests} tests
                              </span>
                            );
                          }
                        })()}
                      </td>
                      <td>
                        {(() => {
                          let testDurationValue = pipeline.totalTestDuration;
                          let testDurationUnitLabel = 's';
                          
                          if (durationUnit === 'm') {
                            testDurationValue = pipeline.totalTestDuration / 60;
                            testDurationUnitLabel = 'm';
                          } else if (durationUnit === 'h') {
                            testDurationValue = pipeline.totalTestDuration / 3600;
                            testDurationUnitLabel = 'h';
                          }
                          
                          return `${testDurationValue.toFixed(1)}${testDurationUnitLabel}`;
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-center text-muted py-4">
            <p>No scheduled pipeline data available to display in the table.</p>
            {selectedDate !== 'all' && (
              <p>Try selecting "All Dates" or a different date to see pipeline data.</p>
            )}
          </div>
        )}
      </Card.Body>
      </Card>
    </>
  );
};

export default RegressionsTab; 